import { createContext, useContext } from "react";
import type { HeartRate } from "./use-heart-rate-link";

/**
 * THE ONE CONNECTION, REACHABLE FROM ANY SCREEN INSIDE A SESSION.
 *
 * 🚨 THE POINT OF THE CONTEXT IS THAT THERE MUST BE EXACTLY ONE. Two components each calling
 * `useHeartRateLink()` would be two state machines racing for one radio: two `connect()` calls,
 * two subscriptions to the same characteristic, two reconnect loops sharing one attempt budget,
 * and two independent sample buffers — so whichever screen happened to be mounted when the
 * debrief ran would decide which half of the session got recorded.
 *
 * So the machine is instantiated in exactly one place, `HeartRateProvider`, above the whole
 * signed-in app, and everything else reads it through here.
 *
 * ═══ WHY A CONTEXT AND NOT A STORE ═══
 *
 * A zustand store would also give one instance, and this project keeps interface state in
 * stores. It is the wrong tool here for a specific reason: this machine owns EFFECTS with a
 * lifetime — timers, a GATT subscription, a teardown that must run when the app is left — and
 * those belong to a mounted component, not to a module that is alive from the moment it is
 * first imported. A store holding a live Bluetooth connection has no unmount to release it on.
 */

/**
 * `null` means "no provider above this component", which is a wiring mistake rather than a
 * state. It is distinguishable from a real value on purpose — see {@link useHeartRate}.
 */
export const HeartRateContext = createContext<HeartRate | null>(null);

/**
 * THE LIVE READING AND ITS CONTROLS, from wherever you are.
 *
 * ⚠️ IT THROWS WITH NO PROVIDER RATHER THAN ANSWERING A HARMLESS-LOOKING DEFAULT. An "idle,
 * no beats, connect does nothing" fallback is indistinguishable at every call site from a strap
 * that is simply not paired — so a screen rendered outside the provider would look completely
 * normal, offer a connect button that silently did nothing, and record an empty trace. Failing
 * loudly at the boundary is the only version of this that gets found in development.
 */
export function useHeartRate(): HeartRate {
  const value = useContext(HeartRateContext);
  if (value === null) {
    throw new Error("useHeartRate must be used inside <HeartRateProvider>");
  }
  return value;
}
